<template>
  <Teleport to="body">
    <div v-if="open" class="int-modal-overlay" @click.self="$emit('close')">
      <div class="int-modal int-modal--lg" role="dialog" aria-modal="true" @click.stop>
        <div class="int-modal__accent" />
        <header class="int-modal__header">
          <div class="int-modal__icon-wrap">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <div class="int-modal__titles">
            <h2>{{ isEdit ? t('integracao.agendamentoForms.editTitle') : t('integracao.agendamentoForms.createTitle') }}</h2>
            <p class="int-modal__subtitle">{{ t('integracao.agendamentoForms.editorSubtitle') }}</p>
          </div>
          <button type="button" class="int-modal__close" :aria-label="t('common.close')" @click="$emit('close')">
            <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </header>

        <div class="int-modal__body">
          <section class="int-modal__section">
            <div class="int-modal__field">
              <label for="ag-nome">{{ t('integracao.forms.formName') }}</label>
              <input id="ag-nome" v-model="draft.nome" type="text" />
            </div>
            <div
              class="int-modal__field"
              :class="{ 'int-modal__field--error': motivoFieldError }"
            >
              <label for="ag-motivo">
                {{ t('integracao.agendamentoForms.motivoPadrao') }}
                <span class="int-modal__required" aria-hidden="true">*</span>
              </label>
              <select
                id="ag-motivo"
                v-model="draft.motivoPadrao"
                class="int-modal__select"
                :disabled="!motivosAtivos.length"
                :aria-invalid="motivoFieldError"
                :aria-describedby="motivoDescribedBy"
                @change="onMotivoChange"
                @blur="motivoTouched = true"
              >
                <option value="">{{ t('integracao.agendamentoForms.motivoSelectPlaceholder') }}</option>
                <option
                  v-if="draft.motivoPadrao && !motivosAtivos.some(m => m.motivo === draft.motivoPadrao)"
                  :value="draft.motivoPadrao"
                >
                  {{ draft.motivoPadrao }}
                </option>
                <option
                  v-for="m in motivosAtivos"
                  :key="m.id"
                  :value="m.motivo"
                >
                  {{ m.icone ? `${getMotivoIcon(m.icone)} ` : '' }}{{ m.motivo }}
                </option>
              </select>
              <p
                v-if="motivoShowEmptyCatalog"
                id="ag-motivo-empty"
                class="int-modal__field-error int-modal__field-error--block"
                role="alert"
              >
                {{ t('integracao.agendamentoForms.motivoEmptyHint') }}
                <router-link
                  v-if="workspaceId"
                  :to="configMotivosRoute"
                  class="int-modal__hint-link"
                  @click="$emit('close')"
                >
                  {{ t('integracao.agendamentoForms.motivoGoToSettings') }}
                </router-link>
              </p>
              <p
                v-else-if="motivoShowError"
                id="ag-motivo-error"
                class="int-modal__field-error"
                role="alert"
              >
                {{ t('integracao.agendamentoForms.motivoRequired') }}
              </p>
            </div>
            <div class="int-modal__field">
              <label for="ag-success">{{ t('integracao.forms.successMessage') }}</label>
              <textarea id="ag-success" v-model="draft.successMessage" rows="3" />
            </div>
            <div v-if="isEdit" class="int-modal__status-row">
              <span class="int-modal__status-label">{{ t('integracao.forms.active') }}</span>
              <label class="int-toggle"><input v-model="draft.ativo" type="checkbox" /><span class="int-toggle__track"><span class="int-toggle__thumb" /></span></label>
            </div>
          </section>

          <section
            class="int-modal__section"
            :class="{ 'int-modal__section--error': scheduleTouched && !hasScheduleSlots }"
          >
            <div class="int-schedule-summary-row">
              <div class="int-schedule-summary-row__info">
                <h3 class="int-modal__section-title">{{ t('integracao.agendamentoForms.scheduleTitle') }}</h3>
                <p v-if="scheduleSummary" class="int-schedule-summary">{{ scheduleSummary }}</p>
                <p v-else class="int-modal__section-hint">{{ t('integracao.agendamentoForms.scheduleNotConfigured') }}</p>
                <p v-if="scheduleTouched && !hasScheduleSlots" class="int-modal__field-error" role="alert">
                  {{ t('integracao.agendamentoForms.scheduleRequired') }}
                </p>
              </div>
              <button type="button" class="int-btn int-btn--secondary int-schedule-summary-row__btn" @click="scheduleModalOpen = true">
                {{ t('integracao.agendamentoForms.configureSchedule') }}
              </button>
            </div>
          </section>

          <section class="int-modal__section">
            <h3 class="int-modal__section-title">{{ t('integracao.forms.fieldsTitle') }}</h3>
            <p v-if="isCrm" class="int-modal__hint">{{ t('integracao.forms.fieldsHint') }}</p>
            <div class="int-field-list">
              <div
                v-for="key in fieldKeys"
                :key="key"
                class="int-field-card"
                :class="{
                  'int-field-card--locked': lockedKeys.includes(key),
                  'int-field-card--disabled': !draft.fieldConfig[key]?.enabled && key !== 'nome'
                }"
              >
                <div class="int-field-card__info">
                  <span v-if="isCrm" class="int-field-card__icon">{{ fieldAbbr(key) }}</span>
                  <div>
                    <div class="int-field-card__name">{{ fieldLabel(key) }}</div>
                    <div v-if="key === 'nome'" class="int-field-card__badge">{{ t('integracao.forms.alwaysRequired') }}</div>
                  </div>
                </div>
                <div class="int-field-card__toggles">
                  <label class="int-toggle" :class="{ 'int-toggle--disabled': lockedKeys.includes(key) || !canDisable(key) }">
                    <input type="checkbox" :checked="draft.fieldConfig[key].enabled" :disabled="lockedKeys.includes(key) || !canDisable(key)" @change="toggleEnabled(key, $event.target.checked)" />
                    <span class="int-toggle__track"><span class="int-toggle__thumb" /></span>
                    <span v-if="isCrm" class="int-toggle__label">{{ t('integracao.forms.enabled') }}</span>
                  </label>
                  <label
                    v-if="isCrm && canOptional(key)"
                    class="int-toggle"
                    :class="{ 'int-toggle--disabled': !draft.fieldConfig[key].enabled }"
                  >
                    <input v-model="draft.fieldConfig[key].optional" type="checkbox" :disabled="!draft.fieldConfig[key].enabled" />
                    <span class="int-toggle__track"><span class="int-toggle__thumb" /></span>
                    <span class="int-toggle__label">{{ t('integracao.forms.optional') }}</span>
                  </label>
                </div>
              </div>
            </div>
          </section>
          <div v-if="configError" class="int-modal__alert int-modal__alert--error">{{ configError }}</div>
        </div>

        <footer class="int-modal__footer">
          <button type="button" class="int-btn int-btn--ghost" @click="$emit('close')">{{ t('common.cancel') }}</button>
          <button type="button" class="int-btn int-btn--primary" :disabled="saving" @click="save">{{ saving ? t('integracao.forms.saving') : t('common.save') }}</button>
        </footer>
      </div>

      <AgendamentoScheduleEditorModal
        :open="scheduleModalOpen"
        :schedule-config="draft.scheduleConfig"
        @close="scheduleModalOpen = false"
        @apply="onScheduleApply"
      />
    </div>
  </Teleport>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { getTagIcon } from '@/utils/iconMapper'
import {
  AGENDAMENTO_FORM_FIELD_KEYS,
  defaultAgendamentoFieldConfig,
  normalizeAgendamentoFieldConfig,
  canDisableAgendamentoField
} from '@/utils/agendamentoFormFieldConfig'
import {
  FORM_FIELD_KEYS,
  defaultFieldConfig,
  normalizeFieldConfig,
  canDisableField,
  canToggleOptional
} from '@/utils/leadFormFieldConfig'
import {
  defaultScheduleConfig,
  normalizeScheduleConfig,
  hasAnyScheduleSlot,
  formatScheduleSummary
} from '@/utils/agendamentoScheduleConfig'
import AgendamentoScheduleEditorModal from '@/components/integrations/AgendamentoScheduleEditorModal.vue'
import '@/assets/styles/integration-modal.css'

const props = defineProps({
  open: Boolean,
  initial: { type: Object, default: null },
  saving: Boolean,
  workspaceId: { type: [Number, String], default: null },
  workspaceTipo: { type: String, default: 'SERVICOS' },
  motivos: { type: Array, default: () => [] }
})
const emit = defineEmits(['close', 'save'])
const { t } = useI18n()

const isCrm = computed(() => props.workspaceTipo === 'CRM')
const fieldKeys = computed(() =>
  isCrm.value
    ? FORM_FIELD_KEYS
    : AGENDAMENTO_FORM_FIELD_KEYS.filter(k => !['dataAgendamento', 'horario'].includes(k))
)
const lockedKeys = computed(() => (isCrm.value ? ['nome'] : ['nome', 'dataAgendamento', 'horario']))
const isEdit = computed(() => !!props.initial?.id)
const configError = ref('')
const motivoTouched = ref(false)
const scheduleTouched = ref(false)
const scheduleModalOpen = ref(false)
const draft = ref({
  nome: '',
  motivoPadrao: '',
  successMessage: '',
  ativo: true,
  fieldConfig: defaultAgendamentoFieldConfig(),
  scheduleConfig: defaultScheduleConfig()
})

const hasScheduleSlots = computed(() => hasAnyScheduleSlot(draft.value.scheduleConfig))

const weekdayLabels = computed(() => {
  const labels = {}
  for (let d = 0; d <= 6; d++) {
    labels[String(d)] = t(`integracao.agendamentoForms.weekdays.${d}`)
  }
  return labels
})

const scheduleSummary = computed(() =>
  formatScheduleSummary(draft.value.scheduleConfig, weekdayLabels.value)
)

const motivosAtivos = computed(() =>
  (props.motivos || []).filter(m => m.ativo !== false)
)

const configMotivosRoute = computed(() => ({
  name: 'WorkspaceConfiguracoes',
  params: { id: String(props.workspaceId) }
}))

const motivoShowEmptyCatalog = computed(() => !motivosAtivos.value.length)

const motivoShowError = computed(() =>
  motivoTouched.value &&
  motivosAtivos.value.length > 0 &&
  !draft.value.motivoPadrao?.trim()
)

const motivoFieldError = computed(() =>
  motivoShowError.value || (motivoShowEmptyCatalog.value && motivoTouched.value)
)

const motivoDescribedBy = computed(() => {
  if (motivoShowEmptyCatalog.value) return 'ag-motivo-empty'
  if (motivoShowError.value) return 'ag-motivo-error'
  return undefined
})

function onMotivoChange () {
  motivoTouched.value = true
  if (draft.value.motivoPadrao?.trim()) configError.value = ''
}

function onScheduleApply (config) {
  draft.value.scheduleConfig = config
  scheduleTouched.value = true
}

function getMotivoIcon (iconeName) {
  if (!iconeName) return ''
  return getTagIcon(iconeName) || iconeName
}

function fieldLabel (key) {
  return isCrm.value
    ? t(`publicForm.fields.${key}`)
    : t(`publicAgendamentoForm.fields.${key}`)
}

function fieldAbbr (key) {
  const map = { nome: 'N', empresa: 'E', cargo: 'C', whatsapp: 'W', email: '@', cnpj: '#' }
  return map[key] || key.slice(0, 1).toUpperCase()
}

function defaultFieldConfigForTipo () {
  return isCrm.value ? defaultFieldConfig() : defaultAgendamentoFieldConfig()
}

function normalizeFieldConfigForTipo (raw) {
  if (isCrm.value) {
    return normalizeFieldConfig({ ...defaultFieldConfig(), ...(raw || {}) })
  }
  return normalizeAgendamentoFieldConfig({ ...defaultAgendamentoFieldConfig(), ...(raw || {}) })
}

watch(() => props.open, (v) => {
  if (!v) return
  configError.value = ''
  motivoTouched.value = false
  scheduleTouched.value = false
  scheduleModalOpen.value = false
  if (props.initial) {
    draft.value = {
      nome: props.initial.nome || '',
      motivoPadrao: props.initial.motivoPadrao || '',
      successMessage: props.initial.successMessage || '',
      ativo: props.initial.ativo !== false,
      fieldConfig: normalizeFieldConfigForTipo(props.initial.fieldConfig),
      scheduleConfig: normalizeScheduleConfig(props.initial.scheduleConfig || defaultScheduleConfig())
    }
  } else {
    draft.value = {
      nome: '',
      motivoPadrao: motivosAtivos.value[0]?.motivo || '',
      successMessage: t('publicAgendamentoForm.defaultSuccess'),
      ativo: true,
      fieldConfig: defaultFieldConfigForTipo(),
      scheduleConfig: defaultScheduleConfig()
    }
  }
})

function canDisable (key) {
  if (isCrm.value) return canDisableField(draft.value.fieldConfig, key)
  return canDisableAgendamentoField(draft.value.fieldConfig, key)
}

function canOptional (key) {
  return isCrm.value && canToggleOptional(key)
}

function toggleEnabled (key, enabled) {
  if (lockedKeys.value.includes(key)) return
  draft.value.fieldConfig[key].enabled = enabled
}

function save () {
  configError.value = ''
  if (!draft.value.nome?.trim()) {
    configError.value = t('integracao.forms.nameRequired')
    return
  }
  if (!motivosAtivos.value.length || !draft.value.motivoPadrao?.trim()) {
    motivoTouched.value = true
    return
  }
  if (!hasScheduleSlots.value) {
    scheduleTouched.value = true
    return
  }
  let normalizedFields
  try {
    normalizedFields = normalizeFieldConfigForTipo(draft.value.fieldConfig)
  } catch {
    configError.value = t('integracao.forms.contactRequired')
    return
  }
  emit('save', {
    nome: draft.value.nome.trim(),
    motivoPadrao: draft.value.motivoPadrao?.trim(),
    successMessage: draft.value.successMessage?.trim(),
    ativo: draft.value.ativo,
    fieldConfig: normalizedFields,
    scheduleConfig: normalizeScheduleConfig(draft.value.scheduleConfig)
  })
}
</script>
